import * as ToastPrimitives from '@radix-ui/react-toast';
import {
	type ComponentPropsWithoutRef,
	type ElementRef,
	forwardRef,
	type ReactElement
} from 'react';
import { type VariantProps, tv } from 'tailwind-variants';

import { cn } from '@/lib/classNames';
import { CheckMarkCircleGreenFilled, XIcon } from '@/resources/assets/images';

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = forwardRef<
	ElementRef<typeof ToastPrimitives.Viewport>,
	ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Viewport
		ref={ref}
		className={cn(
			'fixed right-0 top-0 z-[2000] flex max-h-screen w-full flex-col-reverse gap-2 p-4 md:max-w-[420px]',
			className
		)}
		{...props}
	/>
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = tv({
	base: cn(
		'group pointer-events-auto relative flex w-full text-base font-semibold items-center justify-between space-x-4 overflow-hidden rounded-xl bg-black p-4 shadow-sm transition-all shadow-dm-white/10',
		'data-[swipe=cancel]:translate-x-0',
		'data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=end]:animate-out',
		'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none',
		'data-[state=open]:animate-in data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full',
		'data-[state=closed]:animate-out  data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full '
	),
	variants: {
		variant: {
			success: 'text-dm-green',
			error: 'text-dm-red'
		}
	},
	defaultVariants: {
		variant: 'success'
	}
});

const ToastRoot = forwardRef<
	ElementRef<typeof ToastPrimitives.Root>,
	ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
		VariantProps<typeof toastVariants>
>(({ className, variant = 'success', children, ...props }, ref) => {
	const Icon = variant === 'success' ? CheckMarkCircleGreenFilled : XIcon;

	return (
		<ToastPrimitives.Root
			ref={ref}
			data-variant={variant}
			className={toastVariants({ variant, className })}
			{...props}
		>
			<div className="flex items-center gap-2">
				<Icon
					data-variant={variant}
					className="data-[variant=success]:fill-dm-green h-7 w-7 data-[variant=error]:fill-dm-red"
				/>

				{children}
			</div>
		</ToastPrimitives.Root>
	);
});
ToastRoot.displayName = ToastPrimitives.Root.displayName;

const ToastAction = forwardRef<
	ElementRef<typeof ToastPrimitives.Action>,
	ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Action
		ref={ref}
		className={cn(
			'inline-flex h-8 shrink-0 items-center justify-center rounded-sm border bg-transparent px-3 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
			className
		)}
		{...props}
	/>
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = forwardRef<
	ElementRef<typeof ToastPrimitives.Close>,
	ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Close
		ref={ref}
		className={cn(
			'absolute right-2 top-2 rounded-sm p-0.5 focus:outline-none focus:ring-2',
			className
		)}
		{...props}
	>
		<XIcon className="h-4 w-4 [&_path]:fill-dm-white" />
	</ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = forwardRef<
	ElementRef<typeof ToastPrimitives.Title>,
	ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Title
		ref={ref}
		data-testid="toast-title"
		className={cn('text-sm font-medium', className)}
		{...props}
	/>
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

type ToastProps = ComponentPropsWithoutRef<typeof ToastRoot>;

type ToastActionElement = ReactElement<typeof ToastAction>;

export { type ToastProps, type ToastActionElement };
export const Toast = {
	Provider: ToastProvider,
	Viewport: ToastViewport,
	Root: ToastRoot,
	Title: ToastTitle,
	Close: ToastClose,
	Action: ToastAction
};
